import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ShieldCheck,
  RefreshCw,
  Download,
  Search,
  LogOut,
  Users,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'reset_admin_token';

export default function AdminWaitlist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialToken =
    searchParams.get('token') || localStorage.getItem(TOKEN_KEY) || '';

  const [token, setToken] = useState(initialToken);
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const fetchWaitlist = async (t) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/reset-2027/waitlist`, {
        headers: { 'X-Admin-Token': t },
      });
      if (res.status === 401) {
        setError('Invalid admin token. Try again.');
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
        return;
      }
      if (!res.ok) {
        setError(`Error ${res.status}. Please retry.`);
        return;
      }
      const data = await res.json();
      setRows(data.entries || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
      if (searchParams.get('token')) {
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });
      }
    } catch (_e) {
      setError('Network error. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialToken) fetchWaitlist(initialToken);
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    fetchWaitlist(token.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setRows([]);
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter(
      (r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.source || '').toLowerCase().includes(q),
    );
  }, [rows, query]);

  const downloadCsv = () => {
    const header = ['Name', 'Email', 'Source', 'Submitted'];
    const lines = [header.join(',')];
    filtered.forEach((r) => {
      const row = [
        `"${(r.name || '').replace(/"/g, '""')}"`,
        `"${(r.email || '').replace(/"/g, '""')}"`,
        `"${(r.source || '').replace(/"/g, '""')}"`,
        r.created_at || '',
      ];
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reset-2027-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (iso) => {
    if (!iso) return '\u2014';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch (_e) {
      return iso;
    }
  };

  // --- Auth gate ---
  if (!authed) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md bg-[#111111] border border-white/10 rounded-2xl p-8 md:p-10"
          data-testid="admin-login-form"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-cormorant text-3xl font-medium text-white text-center mb-2">
            Admin Access
          </h1>
          <p className="text-zinc-400 font-manrope text-sm text-center mb-6">
            Enter your admin token to view the Reset 2027 waitlist.
          </p>
          <Input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600 h-12 mb-4"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && (
            <p
              className="text-red-400 font-manrope text-sm mb-4 text-center"
              data-testid="admin-login-error"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || !token.trim()}
            className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded-full font-manrope font-semibold hover:bg-amber-200 disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Verifying\u2026' : 'Unlock'}
          </Button>
        </form>
      </div>
    );
  }

  // --- Dashboard ---
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-waitlist-page">
      <header className="border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center font-cormorant font-bold text-lg">
              R
            </div>
            <div>
              <p className="text-white font-cormorant text-lg leading-tight">
                Admin &middot; 2027 Waitlist
              </p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                The Reset
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="text-zinc-500 hover:text-red-400 font-manrope text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-3">
              The Reset 2027
            </p>
            <h1 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight">
              Early-bird <span className="italic text-[#D4AF37]">waitlist.</span>
            </h1>
            <p className="text-zinc-400 font-manrope text-sm mt-2">
              Brothers who opted in from `/portal/reset-2027`. Deduped on email.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => fetchWaitlist(token)}
              disabled={loading}
              variant="outline"
              className="border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white h-10"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={downloadCsv}
              disabled={!filtered.length}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-amber-200 disabled:opacity-50 h-10"
              data-testid="admin-download-csv"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stat card */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div
            className="rounded-2xl bg-gradient-to-br from-[#D4AF37]/15 via-[#111111] to-[#111111] border border-[#D4AF37]/30 p-5"
            data-testid="stat-total"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-widest font-bold">
                Total on the list
              </p>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="font-cormorant text-5xl font-medium text-white leading-none">
              {rows.length}
            </p>
            <p className="text-zinc-500 font-manrope text-xs mt-2">
              Brothers ready for Panama, August 2027
            </p>
          </div>
          <div className="rounded-2xl bg-[#111111] border border-white/10 p-5">
            <p className="text-zinc-500 font-manrope text-[11px] uppercase tracking-widest font-bold mb-2">
              Latest signup
            </p>
            <p className="font-cormorant text-2xl font-medium text-white leading-tight">
              {rows[0]?.name || 'None yet'}
            </p>
            <p className="text-zinc-500 font-manrope text-xs mt-2">
              {formatDate(rows[0]?.created_at)}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={"Search by name, email, or source\u2026"}
            className="bg-[#111111] border-white/10 text-white placeholder:text-zinc-600 h-11 pl-10"
            data-testid="admin-search-input"
          />
        </div>

        {/* Table */}
        <div
          className="rounded-2xl bg-[#111111] border border-white/10 overflow-hidden"
          data-testid="waitlist-table"
        >
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-zinc-500 font-manrope text-sm">
                {rows.length === 0
                  ? 'No brothers on the waitlist yet.'
                  : 'No matches for that search.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left text-zinc-500 font-manrope text-[11px] uppercase tracking-widest">
                    <th className="px-5 py-3 font-semibold">Name</th>
                    <th className="px-5 py-3 font-semibold">Email</th>
                    <th className="px-5 py-3 font-semibold">Source</th>
                    <th className="px-5 py-3 font-semibold">Submitted</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filtered.map((r) => (
                    <tr
                      key={r.id || r.email}
                      className="hover:bg-white/[0.03] transition-colors"
                      data-testid={`waitlist-row-${r.email}`}
                    >
                      <td className="px-5 py-3 text-white font-manrope">
                        {r.name || '\u2014'}
                      </td>
                      <td className="px-5 py-3 text-zinc-300 font-manrope">
                        <a
                          href={`mailto:${r.email}`}
                          className="hover:text-[#D4AF37] transition-colors"
                        >
                          {r.email}
                        </a>
                      </td>
                      <td className="px-5 py-3 text-zinc-400 font-manrope text-xs">
                        {r.source || '\u2014'}
                      </td>
                      <td className="px-5 py-3 text-zinc-500 font-manrope text-xs whitespace-nowrap">
                        {formatDate(r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-5 py-3 border-t border-white/5 text-zinc-500 font-manrope text-xs">
            Showing {filtered.length} of {rows.length}
          </div>
        </div>
      </div>
    </div>
  );
}
